/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import React, { ReactElement, useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Auth } from '@aws-amplify/auth';
import './styles.scss';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import WPREST from '../../api/bestpark/wp-rest';

import { useTranslate } from 'hooks/translate';

import bestcampWithoutLogo from 'assets/svgs/logos/bestcamp-logo.svg';
import bestcampLogo from 'assets/svgs/logos/bestcamp-logo-with-icon.svg';
import iconUser from 'assets/svgs/icon-user.svg';
import iconLanguage from 'assets/svgs/icon-language.svg';
import Button from 'components/form/button/button';
import Drawer from 'components/animations/drawer';
import Select from 'components/form/select/select';
import useNav from 'hooks/nav';
import { PARKS_PATH, LOGIN_PATH, PROFILE_PATH } from 'constants/paths';
import { TRANSLATIONS } from 'constants/locale';
import { useAuth } from 'contexts/auth-context';
import { useForm } from 'react-hook-form';
import { getPathWithoutLocale } from 'helpers/path-helpers';
import { eraseCookie } from 'helpers/cookie-helper';
import { BESTCAMP_HOST_SLUG } from 'constants/booking';

interface WPMenuItem {
  title: string;
  url: string;
}

/** Menu items. */
interface MenuItem {
  name: string;
  url: string;
  customClass?: string;
}

interface HeaderProps {
  theme?: 'white' | 'blue' | 'charcoal';
  position?: 'relative' | 'absolute';
  hostSlug?: string | undefined;
  hostUrl?: string | null;
  logoUrl?: string;
  color?: string;
}

const menuItems: MenuItem[] = [
  {
    name: 'translations',
    url: PARKS_PATH,
    customClass: 'right translation',
  },
];

function SignOut(): ReactElement {
  const { t } = useTranslate();
  const { setAuthState } = useAuth();

  const doSignOut = async (evt: any): Promise<void> => {
    evt.preventDefault();
    localStorage.clear();
    eraseCookie('bestcamp-user');
    await Auth.signOut();
    await setAuthState(undefined);
  };

  return (
    <span className='sign-out-text' onClick={doSignOut}>
      {t('signout')}
    </span>
  );
}

function UserBanner(): ReactElement {
  const { t, locale } = useTranslate();
  const { authState: auth } = useAuth();
  const { goTo } = useNav();

  const isLoggedin =
    !isUndefined(auth) && !isEmpty(auth) && !isUndefined(auth.userInfo);

  const loginLink = isLoggedin ? `${PROFILE_PATH}` : `${LOGIN_PATH}`;

  return (
    <div className='user-bar'>
      <div className='user'>
        <img
          onClick={() => {
            goTo(loginLink);
          }}
          src={iconUser}
          className='iconmonstr-user-1'
          alt='icon-user'
        />
        <span>
          {isLoggedin
            ? t('landing_page.header.user.hey', auth?.userInfo?.firstName ?? '')
            : t('landing_page.header.user.has_reservation')}
          <Link
            to={
              isLoggedin
                ? `/${locale}${PROFILE_PATH}`
                : `/${locale}${LOGIN_PATH}`
            }
            className='bold-link'
          >
            {isLoggedin
              ? t('landing_page.header.user.bookings')
              : t('login_button')}
          </Link>
        </span>
      </div>
      {isLoggedin && (
        <div className='logout'>
          <SignOut />
        </div>
      )}
    </div>
  );
}

export default function Header({
  theme = 'white',
  position = 'relative',
  hostSlug,
  hostUrl,
  logoUrl,
  color,
}: HeaderProps): ReactElement {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [menus, setMenus] = useState([]);
  const { authState: auth } = useAuth();
  const { t } = useTranslate();
  const history = useHistory();
  const { changeLanguage, locale } = useNav();
  const { register } = useForm({
    defaultValues: {
      translate: `${locale}`,
    },
  });
  useEffect(() => {
    if (hostSlug === BESTCAMP_HOST_SLUG) {
      // Get menu items from WP only in case of BestCamp's own camps
      WPREST({
        method: 'get',
        url: `/menu?lang=${locale}`,
      }).then((response) => {
        setMenus(response);
      });
    }
  }, [locale, hostSlug]);

  const isLoggedin =
    !isUndefined(auth) && !isEmpty(auth) && !isUndefined(auth.userInfo);

  const path = getPathWithoutLocale(history.location.pathname);

  const { REACT_APP_BESTCAMP_WP = '' } = process.env;
  const BESTPARK_WP = `${REACT_APP_BESTCAMP_WP}/${locale}`;
  const headerStyle = color
    ? { position: position, backgroundColor: color }
    : { position };
  const mobileMenuStyleOverride = color ? { backgroundColor: color } : {};
  // Note: do not replace || with ??, since we want bestcamp info in case of empty strings
  const campHostUrl = hostUrl || BESTPARK_WP;
  const campLogoUrl = logoUrl || bestcampLogo;
  const mobileLogoUrl = logoUrl || bestcampWithoutLogo;
  const campLogoHeight = logoUrl ? 100 : 36;
  const campLogoWidth = logoUrl ? 100 : 163;

  return (
    <div className='header-component' style={headerStyle}>
      {path !== PROFILE_PATH && path !== LOGIN_PATH && (
        <div className='user-wrap'>
          <UserBanner />
        </div>
      )}
      {/* Inner header wrapper. */}
      <div className='wrapper'>
        {/* Mobile menu button. Visibile on mobile only. */}
        <button
          className={`btn-mobile-menu open icon-menu ${theme}`}
          onClick={() => setShowMobileMenu((prev) => !prev)}
        >
          <p>{t('menu')}</p>
        </button>
        {/* Site logo desktop. */}
        <a href={campHostUrl} style={{ lineHeight: 0 }} className='brand'>
          <img
            className='logo desktop'
            src={campLogoUrl}
            alt='Main Logo'
            width={campLogoWidth}
            height={campLogoHeight}
          />
        </a>
        {Array.isArray(menus) && (
          <div className={`menu-items desktop ${theme}`}>
            {menus?.map((menuItem: WPMenuItem, idx: Number) => {
              return (
                <a
                  key={`menu-item-desktop-${idx}`}
                  className={`menu-item ${theme}`}
                  href={menuItem.url}
                >
                  {menuItem.title}
                </a>
              );
            })}
            {menuItems.map((menuItem: MenuItem, idx: Number) => {
              return (
                <Select
                  key={`${idx}`}
                  id='translate '
                  label=''
                  name='translate'
                  registerRef={register}
                  options={TRANSLATIONS}
                  action={(e) => {
                    changeLanguage(e.target.value)();
                  }}
                  preIcon={
                    <img
                      src={iconLanguage}
                      className='icon-locale'
                      alt='icon-locale'
                    />
                  }
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Header mobile menu. */}
      <Drawer className='menu-drawer' open={showMobileMenu}>
        <div className='mobile-menu' style={mobileMenuStyleOverride}>
          {/* Close button. */}
          <button
            className='btn-mobile-menu close icon-cancel-1'
            onClick={() => setShowMobileMenu(!showMobileMenu)}
          />

          {/* Mobile menu site logo. */}
          <a href={BESTPARK_WP} style={{ lineHeight: 0 }}>
            <img
              className='logo mobile'
              src={mobileLogoUrl}
              alt='Camp logo'
              width={campLogoWidth}
              height={campLogoHeight}
            />
          </a>
          {/* Menu items. */}
          {Array.isArray(menus) && (
            <div className='menu-items mobile'>
              {menus?.map((menuItem: WPMenuItem, idx: Number) => {
                return (
                  <a
                    key={`menu-item-desktop-${idx}`}
                    className={`menu-item ${theme}`}
                    href={menuItem.url}
                  >
                    {menuItem.title}
                  </a>
                );
              })}

              <ul className='menu-lang'>
                {TRANSLATIONS.map((i, idx) => {
                  return (
                    <li
                      className={locale === i.value ? 'bold' : ''}
                      key={`${idx}`}
                      onClick={() => {
                        changeLanguage(i.value)();
                      }}
                    >
                      {i.name}
                    </li>
                  );
                })}
              </ul>
              <div className='user'>
                {isLoggedin ? (
                  <>
                    <img
                      src={iconUser}
                      className='iconmonstr-user-1'
                      alt='icon-user'
                    />

                    <div className='user-details'>
                      {auth?.userInfo?.firstName} {auth?.userInfo?.lastName}
                      <Link
                        to={`/${locale}${PROFILE_PATH}`}
                        className='bold-link'
                      >
                        {t('landing_page.header.user.check_bookings')}
                      </Link>
                      <div className='logout'>
                        <SignOut />
                      </div>
                    </div>
                  </>
                ) : (
                  <Button
                    className='login-btn primary black'
                    link={`/${locale}${LOGIN_PATH}`}
                    openLinkInSameTab
                  >
                    {t('login_button')}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </Drawer>
      {/* End header mobile menu. */}
    </div>
  );
}
